import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export type ButtonState = "idle" | "loading" | "success" | "error";

type Props = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  state?: ButtonState;
  /** When state="success" or "error", how long to hold before letting parent flip it back. */
  holdMs?: number;
  /** Auto-revert to idle after holdMs. Defaults to false — parent controls state. */
  autoReset?: boolean;
  onStateChange?: (s: ButtonState) => void;

  idleLabel:    React.ReactNode;
  loadingLabel?: React.ReactNode;
  successLabel?: React.ReactNode;
  errorLabel?:   React.ReactNode;

  /** size variant — mirrors Button. */
  size?: "sm" | "md" | "lg";
};

const SIZE: Record<NonNullable<Props["size"]>, string> = {
  sm: "h-9 px-4 text-xs",
  md: "h-10 px-5 text-sm",
  lg: "h-12 px-7 text-base"
};

/**
 * Aceternity stateful-button: morphs between idle / loading / success /
 * error visuals. Each state has its own background colour + icon. The
 * button width animates smoothly between labels.
 */
export const StatefulButton = React.forwardRef<HTMLButtonElement, Props>(
  ({
    state = "idle",
    holdMs = 1400,
    autoReset = false,
    onStateChange,
    idleLabel,
    loadingLabel = "Working…",
    successLabel = "Done",
    errorLabel   = "Try again",
    size = "lg",
    className,
    disabled,
    ...rest
  }, ref) => {
    React.useEffect(() => {
      if (!autoReset) return;
      if (state !== "success" && state !== "error") return;
      const t = window.setTimeout(() => onStateChange?.("idle"), holdMs);
      return () => window.clearTimeout(t);
    }, [state, autoReset, holdMs, onStateChange]);

    const palette = {
      idle:    "bg-accent text-bg hover:bg-accent-hi shadow-[0_0_24px_-8px_#c9a96a]",
      loading: "bg-accent/80 text-bg cursor-wait",
      success: "bg-ok text-bg shadow-[0_0_28px_-6px_#6ad080]",
      error:   "bg-bad text-bg shadow-[0_0_28px_-6px_#e0716a]"
    }[state];

    const isBusy = state !== "idle";

    return (
      <motion.button
        ref={ref}
        layout
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        disabled={disabled || isBusy}
        aria-busy={state === "loading"}
        className={cn(
          "relative inline-flex items-center justify-center gap-2 rounded-full font-medium",
          "transition-colors duration-200",
          "disabled:opacity-100",
          "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg focus-visible:ring-accent",
          SIZE[size],
          palette,
          className
        )}
        {...(rest as any)}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {state === "idle" && (
            <motion.span key="idle" layout
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="flex items-center gap-2">
              {idleLabel}
            </motion.span>
          )}
          {state === "loading" && (
            <motion.span key="loading" layout
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {loadingLabel}
            </motion.span>
          )}
          {state === "success" && (
            <motion.span key="success" layout
              initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }}
              className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {successLabel}
            </motion.span>
          )}
          {state === "error" && (
            <motion.span key="error" layout
              initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.85 }}
              className="flex items-center gap-2">
              <XCircle className="w-4 h-4" /> {errorLabel}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }
);
StatefulButton.displayName = "StatefulButton";
